import { useId, type CSSProperties } from 'react';
import {
  DEFAULT_DESKTOP_LAYOUT,
  desktopBarInteriorClipPath,
  desktopChevronClipPath,
  desktopChevronInteriorClipPath,
  BACKING_NOTCH_FRACTION,
  WASH_NOTCH_FRACTION,
  desktopBackdropClipPath,
  desktopBackdropWidth,
  desktopChevronLeft,
  desktopFillLimit,
  desktopMergedClipPath,
  fillWidth,
  mobileChevronPath,
  mobileFillRight,
  mobileMergedOutlinePath,
  mobilePillBounds,
  mobilePillPath,
  mobilePausedFillPath,
  type DesktopBarLayout,
  type MobileBarLayout,
  type MobileChevronLayout,
} from './complexityBarGeometry';
import { easeOutCubic, useTween } from './useTween';

/**
 * The complexity progress bar and the chevron array, drawn as one figure
 * (MUS-401).
 *
 * Design asked for the bar and the chevrons to read as a single polygon with
 * one continuous border, rather than two shapes that happen to touch.
 *
 * Mobile is new artwork and draws as one stroked SVG path. Desktop preserves
 * the existing array exactly — gradient wash, hairline outlines, no gaps — by
 * keeping its layered `clip-path` technique and changing only the first
 * chevron's polygon. See `complexityBarGeometry.ts` for the shapes.
 *
 * On mobile the figure also animates between two states, per the 2026-08-17
 * design direction:
 *
 * - **paused / first load** — the green alone, narrow, at the top of the
 *   screen. No track, and the chevron pill sits down in the header row.
 * - **playing** — the green thickens and the unfilled track grows leftward out
 *   of the pill to meet it, the pill riding up to the top edge as it goes.
 *
 * Desktop does not have those two states; its figure is static.
 *
 * The green feeds the chevrons rather than competing with them: it runs from
 * the left edge to where the chevron region begins as the user climbs toward
 * the chevron activation threshold, and stops there. Progress past that point
 * is carried by the chevrons lighting up, not by more bar.
 */

/** Chevron green — the same fill the tier selector's completed pill uses. */
const CHEVRON_GREEN = '#36916d';
/** Interior of the figure, matching the header's dark end. */
const FIGURE_DARK = '#0b1523';
/** Border weight of the merged outline. */
const STROKE_WIDTH = 2;

/** Duration of the paused <-> playing choreography. */
const PHASE_MS = 650;

/**
 * How long the fill takes to reach a new value.
 *
 * The band steps once per phrase, so without this the bar jumps. Easing out
 * over roughly a beat reads as filling and receding rather than snapping — the
 * behaviour the bar had before it was folded into this figure.
 *
 * Interim: MUS-401 asks for a constant-rate lag with momentum on sustained
 * climbs and an ease-out into plateaus. This is the ease-out alone.
 */
const FILL_MS = 800;

export type ComplexityPhase = 'paused' | 'playing';

/* ------------------------------------------------------------------ mobile */

/** Strip height while playing. */
const MOBILE_BAR_HEIGHT = 14;
/** Height of the shrunken tempo readout that sits under the strip while playing. */
export const MOBILE_TEMPO_HEIGHT_PX = 24;
/** Gap between the strip's underside and that readout. */
export const MOBILE_TEMPO_GAP_Y_PX = 6;
/** Gap between the readout's right edge and the chevron pill. */
export const MOBILE_TEMPO_GAP_X_PX = 8;
/** Strip height while paused — the "much more narrowed state". */
const MOBILE_PAUSED_BAR_HEIGHT = 6;
/** Pill height at rest. */
const MOBILE_PILL_HEIGHT = 34;
/**
 * Pill height while playing. Taller on purpose: the tempo readout moves in
 * beneath the progress strip during playback, and the pill has to span both so
 * the two read as one block rather than the readout hanging off the bar.
 *
 * Held to exactly strip + gap + readout, so the pill's underside lines up with
 * the readout's rather than landing near it.
 */
const MOBILE_PLAYING_PILL_HEIGHT =
  MOBILE_BAR_HEIGHT + MOBILE_TEMPO_GAP_Y_PX + MOBILE_TEMPO_HEIGHT_PX;
const MOBILE_PILL_WIDTH = 112;
/** Keeps the figure off the screen's left and right edges. */
const MOBILE_SIDE_PADDING = 20;
/**
 * Keeps the figure off the top edge while playing. It only applies then: during
 * playback the header slides away and the figure is the only thing left up
 * there, whereas paused it sits inside a visible header that already spaces it.
 * Scaling it by the phase makes it ease in and out with the rest of the move.
 */
const MOBILE_PLAYING_TOP_PADDING = 10;
const MOBILE_FILLET = 10;
/** How far the pill sits below the top edge while paused, in the header row. */
/**
 * Height of the lesson header at the mobile breakpoint — `--header-height` in
 * Lesson.css.
 */
const MOBILE_HEADER_HEIGHT = 80;
/**
 * How far the pill sits below the top edge while paused.
 *
 * Derived rather than chosen: paused, the header is on screen and the pill sits
 * inside it, so it wants to be centred in that band rather than at some fixed
 * offset from the top.
 */
const MOBILE_PILL_PAUSED_DROP = (MOBILE_HEADER_HEIGHT - MOBILE_PILL_HEIGHT) / 2;
/** Chevrons tiling the mobile pill. */
const MOBILE_CHEVRON_COUNT = 4;
/** Outline weight on the mobile chevrons; also the seam between adjacent ones. */
const MOBILE_CHEVRON_HAIRLINE = 1;
/** Neutral accuracy blue — `accuracyColors[0]` in Lesson.tsx. */
const ACCURACY_NEUTRAL = '#00C2FF';

/** Height of the tempo pill at rest, in the header. */
export const MOBILE_TEMPO_RESTING_HEIGHT_PX = 42;

/**
 * Where the mobile tempo readout sits, in both states.
 *
 * Exported because the readout is DOM — it carries the tempo popup and its
 * app-tour target — while the figure it aligns to is SVG. Both are positioned
 * from the same origin, so sharing these numbers is what keeps the readout's
 * underside on the pill's rather than near it.
 *
 * The horizontal offset is deliberately the same in both states: only the
 * vertical position and the readout's own size change, which keeps the move a
 * transition between two offsets rather than a jump between two layouts.
 */
/**
 * Optical correction, applied in both states.
 *
 * The geometry below puts the readout's box where it belongs, but the pill
 * carries padding and a rounded cap, so its *mark* sits a little lower than its
 * box does and reads as misaligned. Held apart from the derivation so the two
 * stay legible: the arithmetic says where the box goes, this says how far the
 * eye needs it moved.
 */
const MOBILE_TEMPO_NUDGE_Y_PX = 3;

export const MOBILE_TEMPO_SLOT = Object.freeze({
  /** From the screen's right edge, clearing the chevron pill. */
  right: MOBILE_SIDE_PADDING + MOBILE_PILL_WIDTH + MOBILE_TEMPO_GAP_X_PX,
  /** While playing: directly under the progress strip. */
  playingTop:
    MOBILE_PLAYING_TOP_PADDING +
    MOBILE_BAR_HEIGHT +
    MOBILE_TEMPO_GAP_Y_PX -
    MOBILE_TEMPO_NUDGE_Y_PX,
  /** At rest: centred in the header band. */
  restingTop: (MOBILE_HEADER_HEIGHT - MOBILE_TEMPO_RESTING_HEIGHT_PX) / 2 - MOBILE_TEMPO_NUDGE_Y_PX,
});

interface MobileProps {
  width: number;
  fillFraction: number;
  chevronsFilled: number;
  phase: ComplexityPhase;
  accuracyLabel?: string | null;
  accuracyColor?: string;
  accuracyDimmed?: boolean;
  pulsing?: boolean;
  pulseSeconds?: number;
}

const MobileGraphic = ({
  width,
  fillFraction,
  chevronsFilled,
  phase,
  accuracyLabel,
  accuracyColor = ACCURACY_NEUTRAL,
  accuracyDimmed = false,
  pulsing = false,
  pulseSeconds = 1,
}: MobileProps) => {
  const clipId = useId();
  const playing = phase === 'playing';

  // One scalar drives the whole choreography: 0 is fully paused, 1 fully
  // playing. Every dimension below reads off it, so the track, the strip's
  // thickness and the pill's ride all stay locked to each other mid-flight.
  const t = useTween(playing ? 1 : 0, PHASE_MS);
  // The band steps once per phrase, so the fill is eased rather than applied
  // straight; without this the green jumps to each new value.
  const animatedFill = useTween(fillFraction, FILL_MS, easeOutCubic);

  const barHeight = MOBILE_PAUSED_BAR_HEIGHT + (MOBILE_BAR_HEIGHT - MOBILE_PAUSED_BAR_HEIGHT) * t;
  const pillHeight = MOBILE_PILL_HEIGHT + (MOBILE_PLAYING_PILL_HEIGHT - MOBILE_PILL_HEIGHT) * t;
  const pillDrop = MOBILE_PILL_PAUSED_DROP * (1 - t);
  // The track grows leftward out of the pill: at t=0 its left edge sits at the
  // pill's own edge (so the figure is just the pill), at t=1 it has run the
  // full width. The path itself morphs rather than the pill cross-fading, which
  // is what makes the bar look like it grows out of the chevrons.
  //
  // It must reach x=0, not merely the green's edge: the green is the *fill* of
  // this figure, so anything left of the figure is outside the clip and would
  // simply not draw.
  const layout: MobileBarLayout = {
    width,
    barHeight,
    pillHeight,
    pillWidth: MOBILE_PILL_WIDTH,
    rightPadding: MOBILE_SIDE_PADDING,
    leftPadding: MOBILE_SIDE_PADDING,
    filletRadius: MOBILE_FILLET,
  };
  const pillLeft = mobilePillBounds(layout).left;
  // At rest the track is retracted into the pill; fully grown it reaches the
  // strip's left padding rather than the graphic's edge.
  const trackLeft = pillLeft - (pillLeft - MOBILE_SIDE_PADDING) * t;

  // The green tops out just inside the pill, so it reads as feeding the
  // chevrons rather than running underneath them, and no gap shows at the join.
  const fillRight = mobileFillRight(layout, animatedFill);

  const figurePath = mobileMergedOutlinePath(layout, trackLeft);
  // Everything is offset by the top padding; the pill drops further while paused.
  const topPadding = MOBILE_PLAYING_TOP_PADDING * t;
  const pillOffsetY = topPadding + pillDrop;
  // Whichever state reaches lower, so the box never resizes mid-transition.
  const height =
    Math.max(
      MOBILE_PLAYING_TOP_PADDING + MOBILE_PLAYING_PILL_HEIGHT,
      MOBILE_PILL_PAUSED_DROP + MOBILE_PILL_HEIGHT,
    ) + STROKE_WIDTH;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        {/* The fill is a plain rect clipped to the silhouette, so the green
            inherits the strip's rounded cap and the swell into the pill
            without having to know either shape. */}
        <clipPath id={`${clipId}-figure`}>
          <path d={figurePath} transform={`translate(0 ${pillOffsetY})`} />
        </clipPath>
        {/* The pill clips the chevron array, rounding it: the first chevron's
            straight left edge and the last one's straight right edge both
            disappear under the caps. */}
        <clipPath id={`${clipId}-pill`}>
          <path d={mobilePillPath(layout)} />
        </clipPath>
      </defs>

      {/* The green never leaves the top of the screen, so it is laid down
          first as a bare capsule. While playing the figure covers it and the
          clipped fill below restores it; mid-transition this is what shows in
          the stretch the growing track has not reached yet. */}
      <g transform={`translate(0 ${topPadding})`}>
        <path
          d={mobilePausedFillPath(MOBILE_SIDE_PADDING, fillRight, barHeight)}
          fill={CHEVRON_GREEN}
        />
      </g>

      {/* Pill (and, once playing, the track it grows) ride down into the
          header row while paused. The pill keeps its own border throughout —
          it is a bordered pill on the pause screen, not a fragment. */}
      <g transform={`translate(0 ${pillOffsetY})`}>
        <path d={figurePath} fill={FIGURE_DARK} />
      </g>

      {/* The filled head of the merged figure. Clipped to the silhouette so it
          picks up the rounded cap and the swell into the pill. */}
      <g clipPath={`url(#${clipId}-figure)`}>
        <rect
          x={MOBILE_SIDE_PADDING}
          y={0}
          width={Math.max(fillRight - MOBILE_SIDE_PADDING, 0)}
          height={height}
          fill={CHEVRON_GREEN}
        />
      </g>

      <g transform={`translate(0 ${pillOffsetY})`}>
        <path d={figurePath} fill="none" stroke={CHEVRON_GREEN} strokeWidth={STROKE_WIDTH} />
        <g clipPath={`url(#${clipId}-pill)`}>
          <MobileChevrons
            left={pillLeft}
            width={MOBILE_PILL_WIDTH}
            height={pillHeight}
            filled={chevronsFilled}
            pulsing={pulsing}
            pulseSeconds={pulseSeconds}
          />
        </g>
        {/* The accuracy readout shares the pill with the chevrons. Drawn here
            so the fixture shows true proportions; once this is wired into the
            header the live `#accuracy` element overlays this slot instead, so
            it keeps its app-tour target and its rest-phrase dimming. */}
        {accuracyLabel ? (
          <text
            x={pillLeft + MOBILE_PILL_WIDTH / 2}
            y={pillHeight / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={20}
            fontWeight={700}
            fill={accuracyColor}
            opacity={accuracyDimmed ? 0.4 : 1}
          >
            {accuracyLabel}
          </text>
        ) : null}
      </g>
    </svg>
  );
};

/**
 * The mobile chevron array: fillable polygons that tile the pill, matching the
 * desktop array's semantics — green once earned, dark interior until then, with
 * a hairline outline throughout.
 *
 * The array is clipped to the pill by the caller, which is what lets the first
 * chevron take a straight left edge and the last a straight right edge, and
 * lets every chevron overshoot the pill's top and bottom so no outline runs
 * along its edges.
 */
const MobileChevrons = ({
  left,
  width,
  height,
  filled,
  pulsing,
  pulseSeconds,
}: {
  left: number;
  width: number;
  height: number;
  filled: number;
  pulsing: boolean;
  pulseSeconds: number;
}) => {
  const layout: MobileChevronLayout = { left, width, height, count: MOBILE_CHEVRON_COUNT };
  // The chevron about to be earned is the one that pulses — same rule as desktop.
  const pulseIndex = Math.min(filled, MOBILE_CHEVRON_COUNT - 1);

  return (
    <g>
      {Array.from({ length: MOBILE_CHEVRON_COUNT }, (_, index) => {
        const earned = index < filled;
        const pulses = pulsing && !earned && index === pulseIndex;
        const path = mobileChevronPath(layout, index);

        return (
          <g key={index}>
            <path
              d={path}
              // A pulsing chevron sits green underneath and takes a dark
              // overlay whose opacity is animated, exactly as the desktop
              // array flashes `.inner` to reveal the green beneath it.
              fill={earned || pulses ? CHEVRON_GREEN : FIGURE_DARK}
              // Earned chevrons take a dark outline so two of them side by side
              // still show the seam between them, the way the desktop array's
              // hairline gap does. Unearned ones take the green outline that
              // makes the slots still to be won visible.
              stroke={earned ? FIGURE_DARK : CHEVRON_GREEN}
              strokeWidth={MOBILE_CHEVRON_HAIRLINE}
              strokeLinejoin="round"
            />
            {pulses ? (
              <path
                d={path}
                fill={FIGURE_DARK}
                style={{ animation: `pulse ${pulseSeconds}s infinite linear` }}
              />
            ) : null}
          </g>
        );
      })}
    </g>
  );
};

/* ----------------------------------------------------------------- desktop */

/** Unfilled chevron interior — `.inner` in the original stylesheet. */
const DESKTOP_INNER_DARK = '#1c2d42';
/** Wash over the array, left-dark to transparent — the original `#gradient`. */
const DESKTOP_WASH = 'linear-gradient(90deg, #00000065, transparent)';
/** Backing behind the array — the original `#chevron-bg-fill`. */
const DESKTOP_BACKING = 'black';
/**
 * The accuracy readout over the array — `.accuracy-text-styles` at the desktop
 * breakpoint, matched exactly. Family is deliberately unset so it inherits from
 * the app the way the original does.
 */
const DESKTOP_ACCURACY_TEXT: CSSProperties = {
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '50px',
  letterSpacing: '0.01em',
  marginTop: '-5px',
};
/** Chevrons in the array, including the one merged with the strip. */
const DESKTOP_CHEVRON_COUNT = 4;

interface DesktopProps {
  width: number;
  height: number;
  fillFraction: number;
  chevronsFilled: number;
  /** Pulses the next chevron to be earned, as the original array does. */
  pulsing?: boolean;
  /** Seconds per pulse cycle, driven by tempo at the call site. */
  pulseSeconds?: number;
  /** Accuracy readout drawn over the array. */
  accuracyLabel?: string | null;
  /** Its colour, chosen from `accuracyColors` by the call site. */
  accuracyColor?: string;
  /** Dims the readout while a complexity rest phrase plays. */
  accuracyDimmed?: boolean;
}

/**
 * The desktop figure, built the way the original array was: absolutely
 * positioned layers clipped with `clip-path`, an outer green shape showing
 * through an inset dark one to fake a hairline outline, and a gradient wash on
 * top. Only the first chevron differs — its polygon extends left into the
 * progress strip so the two share one silhouette.
 */
const DesktopGraphic = ({
  width,
  height,
  fillFraction,
  chevronsFilled,
  pulsing = false,
  pulseSeconds = 1,
  accuracyLabel = null,
  accuracyColor = ACCURACY_NEUTRAL,
  accuracyDimmed = false,
}: DesktopProps) => {
  const layout: DesktopBarLayout = { ...DEFAULT_DESKTOP_LAYOUT, width, height };
  const animatedFill = useTween(fillFraction, FILL_MS, easeOutCubic);

  // The green stops at the first chevron's left edge and feeds it, rather than
  // running the width of the header underneath the whole array.
  const filled = fillWidth(desktopFillLimit(layout), animatedFill);

  const layer = (clipPath: string, background: string): CSSProperties => ({
    position: 'absolute',
    inset: 0,
    clipPath,
    background,
  });

  // The backdrop layers get their own box at the array rather than spanning the
  // header, so the wash's gradient ramps across the chevrons instead of spending
  // itself on the progress strip and arriving transparent.
  const backdropLayer = (notchFraction: number, background: string): CSSProperties => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: `${desktopChevronLeft(layout)}px`,
    width: `${desktopBackdropWidth(layout)}px`,
    clipPath: desktopBackdropClipPath(layout, notchFraction),
    background,
  });

  // Index 0 is merged with the strip and is drawn by the merged layers below.
  const trailing = Array.from({ length: DESKTOP_CHEVRON_COUNT - 1 }, (_, i) => i + 1);
  // The array's last chevron is a half chevron: it keeps the notch that receives
  // the previous chevron's point but has none of its own, since its right edge
  // falls off the side of the screen.
  const isLast = (index: number) => index === DESKTOP_CHEVRON_COUNT - 1;
  // The chevron about to be earned is the one that pulses.
  const pulseIndex = Math.min(chevronsFilled, DESKTOP_CHEVRON_COUNT - 1);

  return (
    // `overflow: hidden` is what actually puts the "off screen" edges off
    // screen: the squared last chevron, the trailing chevron's point and the
    // backdrop's overhang all extend past the header deliberately, and without
    // this they would paint outside it and could force a horizontal scrollbar.
    <div
      style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}
      aria-hidden
    >
      {/* Backing behind the array. It starts at the chevron region so it never
          darkens the progress strip. */}
      <div style={backdropLayer(BACKING_NOTCH_FRACTION, DESKTOP_BACKING)} />

      {/* Outer green: the border, and the fill colour that earned chevrons
          reveal by leaving their interior transparent. */}
      <div style={layer(desktopMergedClipPath(layout), CHEVRON_GREEN)} />
      {trailing.map((index) => (
        <div
          key={`outer-${index}`}
          style={layer(desktopChevronClipPath(layout, index, isLast(index)), CHEVRON_GREEN)}
        />
      ))}

      {/* Inset dark interiors. An earned chevron simply omits its own, letting
          the green beneath show through — the original `.inner.filled`. */}
      <div style={layer(desktopBarInteriorClipPath(layout), DESKTOP_INNER_DARK)} />
      {chevronsFilled < 1 ? (
        <div style={layer(desktopChevronInteriorClipPath(layout, 0), DESKTOP_INNER_DARK)} />
      ) : null}
      {trailing
        .filter((index) => index >= chevronsFilled)
        .map((index) => (
          <div
            key={`inner-${index}`}
            style={{
              ...layer(
                desktopChevronInteriorClipPath(layout, index, isLast(index)),
                DESKTOP_INNER_DARK,
              ),
              ...(pulsing && index === pulseIndex
                ? { animation: `pulse ${pulseSeconds}s infinite linear` }
                : {}),
            }}
          />
        ))}

      {/* The progress fill, clipped to the strip's interior so it picks up the
          hairline and stops against the chevron's flank. */}
      <div
        style={{
          ...layer(desktopBarInteriorClipPath(layout), CHEVRON_GREEN),
          right: `${width - filled}px`,
        }}
      />

      {/* Wash, clipped to the array for the same reason as the backing — over
          the full header it would dim the strip along with the chevrons. */}
      <div style={backdropLayer(WASH_NOTCH_FRACTION, DESKTOP_WASH)} />

      {/* Accuracy readout, centred over the reserved region the way
          `.accuracy-container-styles2` centres it over the accuracy box. */}
      {accuracyLabel ? (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${desktopChevronLeft(layout)}px`,
            width: `${layout.chevronRegionWidth}px`,
            marginLeft: '-8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...DESKTOP_ACCURACY_TEXT,
            color: accuracyColor,
            // Rest phrases do not count toward accuracy, so the readout is
            // visibly de-emphasised for their duration.
            opacity: accuracyDimmed ? 0.4 : 1,
            transition: 'opacity 0.3s ease-in-out',
          }}
        >
          {accuracyLabel}
        </div>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------- root */

interface ComplexityHeaderGraphicProps {
  variant: 'mobile' | 'desktop';
  /** Width of the figure in CSS pixels. */
  width: number;
  /** Header height. Desktop only — the chevrons span it. */
  height?: number;
  /**
   * Progress toward chevron activation, in [0, 1] — *not* progress through the
   * tier. 1 means the user has reached the chevron activation threshold and the
   * green has arrived at the first chevron; past that point the chevrons carry
   * progress and this stays pinned at 1.
   */
  fillFraction: number;
  /** Chevrons currently earned, 0-4. */
  chevronsFilled: number;
  /** Mobile only: drives the paused <-> playing choreography. */
  phase?: ComplexityPhase;
  /**
   * Mobile only: accuracy text drawn in the pill's reserved slot. Pass `null`
   * when the live `#accuracy` element is overlaid there instead.
   */
  accuracyLabel?: string | null;
  /** Colour of the accuracy readout, from `accuracyColors` at the call site. */
  accuracyColor?: string;
  /** Dims the readout while a complexity rest phrase plays. */
  accuracyDimmed?: boolean;
  /** Pulse the next chevron to be earned, as the array does today. */
  pulsing?: boolean;
  /** Desktop only: seconds per pulse cycle, derived from tempo at the call site. */
  pulseSeconds?: number;
}

const ComplexityHeaderGraphic = ({
  variant,
  width,
  height = 160,
  fillFraction,
  chevronsFilled,
  phase = 'paused',
  accuracyLabel = null,
  accuracyColor = ACCURACY_NEUTRAL,
  accuracyDimmed = false,
  pulsing = false,
  pulseSeconds = 1,
}: ComplexityHeaderGraphicProps) => {
  if (variant === 'mobile') {
    return (
      <MobileGraphic
        width={width}
        fillFraction={fillFraction}
        chevronsFilled={chevronsFilled}
        phase={phase}
        accuracyLabel={accuracyLabel}
        accuracyColor={accuracyColor}
        accuracyDimmed={accuracyDimmed}
        pulsing={pulsing}
        pulseSeconds={pulseSeconds}
      />
    );
  }
  return (
    <DesktopGraphic
      width={width}
      height={height}
      fillFraction={fillFraction}
      chevronsFilled={chevronsFilled}
      pulsing={pulsing}
      pulseSeconds={pulseSeconds}
      accuracyLabel={accuracyLabel}
      accuracyColor={accuracyColor}
      accuracyDimmed={accuracyDimmed}
    />
  );
};

export default ComplexityHeaderGraphic;
